import BaseFinancingForm from '../../base-financing-form';

import {parseDecimalNumber} from '../../number-helpers';
import { isNumber } from '../../tools';

/**
 * PartPaymentFinancingForm handles Financing form functionalities.
 */
export default class PartPaymentFinancingForm extends BaseFinancingForm {

  /**
   * @property {HTMLElement} all class instance functionality is scoped under this one element
   */
   #element;

   /**
    * @property {CarPageApp} carPageApp instance for relating state changes accross the pag
    */
   #carPageApp;

  /**
   * @readonly
   * @property {object} state object containing carData and related information this view
   */
  #state;

  /**
   * @constructor
   * @param {HTMLElement} element element to bind to
   * @param {HTMLElement} carPageApp state object
   */ 
  constructor(element, carPageApp) {
    super();

    this.#carPageApp = carPageApp;
    this.#element = element;
    this.#findElements();
    this.#bind();
    this.#initState();
  }

  /**
   * Return CarPageApp state object part name
   * @public
   * @return {string} state part name
   */
  get statePartName() {
    return 'partPaymentCalculatorSettings';
  }

  /**
   * Return users selection from DOM ready and format data ready to be pass down
   * to ajax-interface.
   * Keep attribute names in the data intact, otherwise they are passed ajax interface with wrong names ;)
   * @public
   * @return {object} formatted data to ajax interface
   */
  get selections() {

    const data = {
      'car_category': this.#state.carData.car_category,
      'year_model': this.#state.carData.year ,
      'price': this.#state.carData.price.price,
      'netwheels_id': this.#state.carData.netwheels_id,

      ...this.userSelections,

      user_downpayment: parseDecimalNumber( this.userSelections.user_downpayment ),
      finalpayment: parseDecimalNumber( this.userSelections.finalpayment )
    }

    return data;
  }

  /**
   * Set financing duration radio input value
   * @param {string} value financial duration value
   */
  setFinancingDurationValue( value ) {
    this.elements.financingDurations.forEach( (element) => {
      element.checked = element.value === value;
    });
  }

  /**
   * @returns {string} Get financing duration radio input value
   */
  getFinancingDurationValue() {
    return this.elements.financingDurations.find( (element) => element.checked )?.value;
  }

  /**
   * Get user selections from DOM
   * @public
   * @return {object} selections
   */
  get userSelections() {
    return {
      payments: this.getFinancingDurationValue(),

      //Fields hold values like "2 000,3" needs little special handling before are converted to numbers
      //user_downpayment: parseDecimalNumber( this.elements.downPayment.value ),
      //finalpayment: parseDecimalNumber( this.elements.finalPayment.value )
      user_downpayment: this.elements.downPayment.value,
      finalpayment: this.elements.finalPayment.value
    }
  }

  /**
   * Set user changable elements values trough this setter.
   * @public
   * @param {object} data partPaymentCalculatorSettings state object
   */
  set userSelections( data ) {
    this.setFinancingDurationValue(data.payments);
    this.elements.finalPayment.value = data.finalpayment;
    this.elements.downPayment.value = data.user_downpayment;
  }

  /**
   * Update extra information data to DOM
   * @public
   * @param {object} data extra information data
   */
  set extrainformation( data ) {
    this.elements.price.dataset.value = data.price;
    this.elements.totalLoanSum.dataset.value = data.totalLoanSum;
    this.elements.realInterest.dataset.value = data.realInterest;
    this.elements.totalSum.dataset.value = data.totalSum;
    this.elements.duration.dataset.value = data.payments;
  }

  /**
   * Called before financing query is send, cather financing query parameters here from the HTMLFormElement
   * @override
   * @public
   * @return {URLSearchParams} responseJSON financing query response json
   */
  get data() {
    return new URLSearchParams(this.selections)
  }

  /**
   * Find all elements from dom for accessing later on.
   * @private
   */
  #findElements() {
    this.elements = {
      financeInfoAccordion: this.#element.querySelector('.hedin-accordion') ,
      financingDurations: Array.from( this.#element.querySelectorAll('#partpayment-financing-duration input') ),
      downPayment: this.#element.querySelector('input[name="downpayment"]'),
      finalPayment: this.#element.querySelector('input[name="finalpayment"]'),
      financeFormShowFinanceText: this.#element.querySelector('.show-finance-text'),
      totalLoanSum: this.#element.querySelector('.finance-value.total-loan-sum'),
      realInterest: this.#element.querySelector('.finance-value.real-interest'),
      totalSum: this.#element.querySelector('.finance-value.total-sum'),
      duration: this.#element.querySelector('.finance-value.payments'),
      price: this.#element.querySelector('.price'),

      downPaymentField: this.#element.querySelector('input[name="downpayment"]').closest('.form-v10__field'),
      finalPaymentField: this.#element.querySelector('input[name="finalpayment"]').closest('.form-v10__field'),

      financingLink: this.#element.querySelector('#financing-link'),
    }
  }

  /**
   * Bind all event listeners function to DOM
   * @private
   */
  #bind() {
    this.elements.financingDurations?.forEach( (element) => {
      element.addEventListener('change', this.#onValueChanged );
    });
    this.elements.financeInfoAccordion?.addEventListener('hedin-accordion-toggle', this.#onShowFinancingLinkClicked.bind(this) )
    this.elements.finalPayment?.addEventListener('change', this.#onValueChanged );
    this.elements.downPayment?.addEventListener('change', this.#onValueChanged);
  }

  /**
   * Called when a user makes a change to some field. Start ajax financing query to Stage side.
   */
  #onValueChanged = () => {
    this.debouncedFinancingQuery();
  }

  /**
   * Open and closes financial extpra information area.
   * @private
   * @param {Event} event event object
   */
  #onShowFinancingLinkClicked = (event) => {
    const state = this.#filterState( this.#carPageApp.getState() );
    const financeInfoState = state.financeInfo === 'open' ? '' : 'open';
    this.#carPageApp.updateState( `${this.statePartName}.financeInfo`, financeInfoState)
  }

  /**
  * Called after financing query get a server response from Stage ajax interface.
  * This is a good place to update values in the form.
  * @override
  * @protected
  * @param {object} calculations financing query response from State
  */
  _afterSearch( calculations ) {

    //Normalize data format before is it saved to state managemet
    const data = {
      downPayment: calculations.downPayment,
      finalPayment: calculations.lastPayment,
      price: calculations.monthlyPriceFormatted,
      totalLoanSum: calculations.loanAmount,
      realInterest: calculations.realAnnualInterest,
      totalSum: calculations.total,
    };

    // Set final payment max value if set value is above it
    if (calculations.downPayment > calculations.maxDownPayment){
      data.downPayment = calculations.maxDownPayment;
      this.elements.downPayment.value = calculations.maxDownPayment;
    }

    // Set final payment max value if set value is above it
    if (calculations.lastPayment > calculations.maxLastPayment){
      data.finalPayment = calculations.maxLastPayment;
      this.elements.finalPayment.value = calculations.maxLastPayment;
    }

    this.#carPageApp.updateState(this.statePartName, {
      financeInfo: this.#state.financeInfo,
      userSelections: this.userSelections,//User made selections
      calculations: data//Response object from Stage's get-finance-info interface
    }, true);
  }

  // --- State management ---

  /**
   * Init state manament from a values in local storage. If nothing is found values set in Stage is used.
   * @private
   */
  #initState() {
    //Update state from state manager's local storage
    const partPaymentCalculatorSettings = this.#filterState(this.#carPageApp.getState());

    if( Object.keys(partPaymentCalculatorSettings).length > 0 ) {
      if( partPaymentCalculatorSettings.userSelections ) {
        this.userSelections = partPaymentCalculatorSettings.userSelections;
      }
      this.#onStateChanges( partPaymentCalculatorSettings );
    }

    //Register observer after state is loaded from state manager.
    this.#carPageApp.registerStateObserver( (fullStateObject) => this.#handleStateChanges(fullStateObject) );
  }

  /**
   * On state changes this function is called by CarPageApp instance
   * @private
   * @param {object} state filtered state object
   */
  #onStateChanges(state) {

    this.#state = state;

    if(state.calculations) {
      this.extrainformation = { ...state.calculations, ...state.userSelections };
    }

    if( state.userSelections ) {

      const userDownpayment = parseDecimalNumber( state.userSelections?.user_downpayment );
      const finalpayment = parseDecimalNumber( state.userSelections?.finalpayment );

      const userDownPaymentError = !isNumber(userDownpayment) || userDownpayment < 0;
      const finalPaymentError = !isNumber(finalpayment) || finalpayment < 0;

      this.elements.downPaymentField.classList.toggle('stage-error', userDownPaymentError);
      this.elements.finalPaymentField.classList.toggle('stage-error', finalPaymentError);

      if( state.calculations && (userDownPaymentError || finalPaymentError) ) {
        this.extrainformation ={ ...state.calculations, ...state.userSelections, price: NaN };
      }
    }

    this.elements.financeInfoAccordion.accordion.open = state.financeInfo === 'open';

    if (state.financeInfo === 'open') {
      this.elements.financeInfoAccordion.accordion.text = 'Piilota osamaksutiedot';
    } else {
      this.elements.financeInfoAccordion.accordion.text = 'Näytä osamaksutiedot';
    }
  }

  /**
   * Filter state object part that is needed in this particular class.
   * @private
   * @param {object} state CarPageApp state object
   * @returns {object|undefined} return if found
   */
  #filterState(state) {
    // eslint-disable-next-line no-prototype-builtins
    if (typeof state === 'object' && state.hasOwnProperty(this.statePartName)) {
      const stateObject = state[this.statePartName];
      stateObject.carData = state.carData;
      return stateObject;
    }
  }

  /**
   * CarPageApp observer function & event listener.
   * @param {object} fullStateObject full state object
   */
  #handleStateChanges( fullStateObject ) {
    const filteredStateObject = this.#filterState(fullStateObject);
    if( Object.keys(filteredStateObject).length > 0 ) {
      this.#onStateChanges( filteredStateObject );
    }
  }

  /**
   * Static function that is callable from master.js. Instantiate LaakkonenFinancingForm.
   * @public
   * @param {CarPageApp} carPageApp state object
   */
  static init(carPageApp) {
    document.querySelectorAll('.laakkonen-financing-form').forEach( element => {
      new PartPaymentFinancingForm( element, carPageApp );
    });
  }
}


